import {
  createContext, useCallback, useContext, useEffect, useState, type ReactNode,
} from 'react';
import { api, ApiError, type User } from '../shared/api';
import { browserSupportsWebAuthn, startAuthentication } from '../shared/webauthn-browser';

interface AuthState {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthCtx = createContext<AuthState>({
  user: null,
  loading: true,
  signIn: async () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Probe the existing cookie session on load; a 401 simply means signed out.
    api.get<User>('/auth/me')
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const res = await api.post<{ user: User }>('/auth/login', { email, password });
    setUser(res.user);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setUser(null);
    }
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, signIn, signOut }}>
      {children}
    </AuthCtx.Provider>
  );
}

/* ── Sign in ───────────────────────────────────────────────────────────── */

const DEMO_ACCOUNTS = [
  ['maker@poaas.io', 'Rahul Raj', 'Creates payment orders'],
  ['verifier@poaas.io', 'Priya Menon', 'Approves at level 1'],
  ['director@poaas.io', 'Rohit Kumar', 'Approves at level 2'],
  ['md@poaas.io', 'Mohit Kumar', 'Approves at level 3'],
  ['ops@poaas.io', 'Sunil Das', 'Publishes and settles'],
  ['auditor@poaas.io', 'Meera Iyer', 'Read-only, verifies the audit chain'],
];

export function SignIn() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('maker@poaas.io');
  const [password, setPassword] = useState('Passw0rd!23');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(email, password);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign in. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const passkeyLogin = async () => {
    if (!email) {
      setError('Enter your email first, then use passkey sign-in.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      // 1. Request authentication options for this email
      const { options } = await api.post<{ options: Record<string, unknown> }>(
        '/auth/passkey/login/options',
        { email },
      );

      // 2. Trigger browser authenticator
      const credential = await startAuthentication(options);

      // 3. Verify with server — creates session
      await api.post<{ user: User }>('/auth/passkey/login/verify', {
        credential,
      });
      // signIn sets the user in context. We can set it directly here since
      // the server already created the session cookie.
      window.location.reload();
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setError('Passkey authentication was cancelled.');
      } else {
        setError(err instanceof ApiError ? err.message : 'Passkey sign-in failed.');
      }
    } finally {
      setBusy(false);
    }
  };

  const supportsPasskey = browserSupportsWebAuthn();

  return (
    <div className="signin">
      <div className="signin-card">
        <div style={{ marginBottom: 30 }}>
          <div className="wordmark" style={{ fontSize: 40 }}>
            POAAS
          </div>
          <div className="wordmark-sub">Payment Order Approval Automation System</div>
        </div>

        <form className="card col gap-14" onSubmit={submit}>
          <div className="field">
            <label className="label" htmlFor="email">Email</label>
            <input
              id="email" className="input" type="email" autoComplete="username"
              value={email} onChange={(e) => setEmail(e.target.value)} required
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="password">Password</label>
            <input
              id="password" className="input" type="password" autoComplete="current-password"
              value={password} onChange={(e) => setPassword(e.target.value)} required
            />
          </div>

          {error && <div className="alert alert-bad" role="alert">{error}</div>}

          <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>

          {supportsPasskey && (
            <>
              <div className="row gap-10" style={{ color: 'var(--ghost)', fontSize: 12 }}>
                <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
                <span>or</span>
                <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
              </div>

              <button
                type="button"
                className="btn btn-ghost btn-block"
                onClick={passkeyLogin}
                disabled={busy}
                style={{ gap: 8 }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0110 0v4"/>
                </svg>
                Sign in with passkey
              </button>
            </>
          )}
        </form>

        <div className="hint mt-16">
          <strong style={{ color: 'var(--paper)' }}>Demo accounts</strong>
          <div style={{ marginTop: 8 }}>
            {DEMO_ACCOUNTS.map(([addr, , role]) => (
              <div key={addr} className="row between" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="btn btn-sm"
                  style={{
                    background: 'none', border: 'none', padding: '2px 0',
                    color: 'var(--seal)', fontFamily: 'var(--data)', fontSize: 11.5,
                  }}
                  onClick={() => setEmail(addr ?? '')}
                >
                  {addr}
                </button>
                <span style={{ fontSize: 11 }}>{role}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 9 }}>
            Password for all accounts: <code>Passw0rd!23</code>
          </div>
        </div>
      </div>
    </div>
  );
}
